import tokenInstructions from '@/assets/token-instruction.png';
import { type RefreshOptions } from '@/hooks/useRefreshUserDataBasics';
import { useRefreshUserDataBasics } from '@/hooks/useRefreshUserDataBasics';
import {
  useAddTokenMutation,
  useChangeModuleTypeMutation,
  useChangeTokenMutation,
} from '@/redux/api/permission-api';
import { useLazyGetUserMeQuery } from '@/redux/api/user-me-api';
import { selectChangeTokenModalProps } from '@/redux/slices/ui-slice';
import { selectUser } from '@/redux/slices/user-slice';
import {
  CATEGORY_INFO,
  ConnectApiTokenErrors,
  DEFAULT_CATEGORIES,
  SALES_CATEGORIES,
  type UiTokenScope,
  validateReadToken,
  validateTokenFormat,
} from '@/utils/token.utils';
import { CommonModuleTypes } from '@marpla/api-sdk';
import Button from '@marpla/ui/components/Button';
import Input from '@marpla/ui/components/Input';
import Link from '@marpla/ui/components/Link';
import { enqueueSnackbar } from '@marpla/ui/components/SnackbarProvider';
import SwitchLabel from '@marpla/ui/components/SwitchLabel';
import Tooltip from '@marpla/ui/components/Tooltip';
import Typography from '@marpla/ui/components/Typography';
import Close from '@marpla/ui/icons/Close';
import { OpenInNew } from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, List, ListItem, ListItemText, Stack } from '@mui/material';
import isFunction from 'lodash/isFunction';
import { type FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import styles from './index.module.scss';
import { Modal } from '../Modal';

export const ChangeTokenModal: FC<ChangeTokenModalProps> = ({
  open,
  mode,
  onClose,
  onSubmitEnd,
  refreshOptions,
}) => {
  const user = useSelector(selectUser);

  useLazyGetUserMeQuery();

  const refreshUserDataBasics = useRefreshUserDataBasics();

  const permissions = user?.permissions;
  const showAddTokenModal = open;
  const activeSettingAccountId = user.accountId;

  const checkedPermission = permissions?.find((p) => p.account._id === activeSettingAccountId);

  const modalProps = useSelector(selectChangeTokenModalProps);
  const permissionId = modalProps?.permissionId ?? '';

  const [moduleTypes, setModuleTypes] = useState(() =>
    !showAddTokenModal && checkedPermission
      ? checkedPermission.moduleTypes
      : [CommonModuleTypes.BIDDER, CommonModuleTypes.ABTEST],
  );

  const isAdd = !checkedPermission;

  const [addToken] = useAddTokenMutation();
  const [changeToken] = useChangeTokenMutation();
  const [changeModuleType] = useChangeModuleTypeMutation();

  const {
    handleSubmit,
    control,
    watch,
    reset,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      apiKey: '',
    },
  });

  useEffect(() => {
    setModuleTypes(
      !isAdd && checkedPermission
        ? checkedPermission.moduleTypes
        : [CommonModuleTypes.BIDDER, CommonModuleTypes.ABTEST],
    );
  }, [checkedPermission, isAdd]);

  const onSubmit = handleSubmit(async ({ apiKey }) => {
    try {
      if (mode === 'add') {
        await addToken({ token: apiKey, moduleTypes }).unwrap();
      } else if (apiKey) {
        await changeToken({
          permissionId,
          token: apiKey,
          moduleTypes,
        }).unwrap();
      } else {
        await changeModuleType({
          permissionId,
          moduleTypes,
        }).unwrap();
      }

      await refreshUserDataBasics(refreshOptions);

      isFunction(onSubmitEnd) ? onSubmitEnd() : onClose();
    } catch (error: any) {
      const message = error?.data?.message;
      enqueueSnackbar(ConnectApiTokenErrors[message] ?? ConnectApiTokenErrors.UNKNOWN, {
        variant: 'error',
      });
    }
  });

  const handleClose = (): void => {
    reset({ apiKey: '' }, { keepDirty: false, keepTouched: false, keepErrors: false });
    clearErrors();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      reset({ apiKey: '' });
      clearErrors();
    }
  }, [open, reset, clearErrors]);

  const requiredCategories = useMemo<readonly UiTokenScope[]>(
    () => (moduleTypes.includes(CommonModuleTypes.SALES) ? SALES_CATEGORIES : DEFAULT_CATEGORIES),
    [moduleTypes],
  );

  return (
    <>
      <Modal show={open} onClick={handleClose} />
      <Box hidden={!open} className={styles.fadeBackground}>
        <Box className={styles.wrapper}>
          <Box className={styles.header} onClick={(e) => e.stopPropagation()}>
            <Typography className={styles.title}>API-ключ</Typography>
            <Close onClick={handleClose} />
          </Box>
          <form onSubmit={onSubmit}>
            <Box className={styles.content}>
              <Link
                to="https://seller.wildberries.ru/supplier-settings/access-to-api"
                blank
                className={styles.link}
              >
                Перейти в раздел управления API-ключами на WB <OpenInNew />
              </Link>
              <Box className={styles.inputWrapper}>
                <Controller
                  control={control}
                  rules={
                    control._formValues.apiKey
                      ? {
                          // required: 'Поле не может быть пустым',
                          pattern: {
                            value: /^[\w-]*\.[\w-]*\.[\w-]*$/,
                            message: 'Неверный формат ключа',
                          },
                          validate: {
                            token: (value) => validateTokenFormat(value),
                            read: (value) => validateReadToken(value),
                          },
                        }
                      : undefined
                  }
                  name="apiKey"
                  render={({ field }) => (
                    <Input
                      {...field}
                      label="Введите новый API ключ"
                      fullWidth
                      autoFocus
                      error={field.value ? Boolean(errors.apiKey) : false}
                      helperText={errors?.apiKey?.message || ''}
                    />
                  )}
                />
              </Box>
              <Box className={styles.instructionImg}>
                <Tooltip
                  classes={{ tooltip: styles.tooltip, arrow: styles.arrow }}
                  placement="right"
                  title={
                    <Box className={styles.instructionImgTooltip}>
                      <img src={tokenInstructions} alt="Инструкция" />
                    </Box>
                  }
                >
                  <img src={tokenInstructions} alt="Инструкция" />
                </Tooltip>
              </Box>
              <Stack
                flexDirection="column"
                gap="8px"
                borderBottom="1px solid #e3e3e3"
                paddingBottom="16px"
              >
                <Typography variant="bodySemibold">Подключаемые модули MARPLA</Typography>
                <Stack direction="row" gap={2} alignItems="center">
                  <SwitchLabel
                    onChange={() =>
                      setModuleTypes((prev) =>
                        prev.includes(CommonModuleTypes.BIDDER)
                          ? prev.filter((c) => c !== CommonModuleTypes.BIDDER)
                          : [...prev, CommonModuleTypes.BIDDER],
                      )
                    }
                    checked={moduleTypes.includes(CommonModuleTypes.BIDDER)}
                    label="Реклама"
                  />
                  <SwitchLabel
                    onChange={() =>
                      setModuleTypes((prev) =>
                        prev.includes(CommonModuleTypes.ABTEST)
                          ? prev.filter((c) => c !== CommonModuleTypes.ABTEST)
                          : [...prev, CommonModuleTypes.ABTEST],
                      )
                    }
                    checked={moduleTypes.includes(CommonModuleTypes.ABTEST)}
                    label="А/В-тесты"
                  />
                  <SwitchLabel
                    onChange={() => {
                      setModuleTypes((prev) =>
                        prev.includes(CommonModuleTypes.SALES)
                          ? prev.filter((c) => c !== CommonModuleTypes.SALES)
                          : [...prev, CommonModuleTypes.SALES],
                      );
                    }}
                    checked={moduleTypes.includes(CommonModuleTypes.SALES)}
                    label="Продажи"
                  />
                </Stack>
              </Stack>
              <List component="ol" className={styles.outerList}>
                <ListItem className={styles.listItem}>
                  <ListItemText
                    sx={{ display: 'list-item' }}
                    primary="В API-ключе нужно подключить в нём следующие категории: "
                  />
                  <List component="ol" disablePadding className={styles.innerList}>
                    {requiredCategories.map((scope) => (
                      <ListItem key={scope}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Typography className={styles.listTextItems}>
                            {CATEGORY_INFO[scope].label}
                          </Typography>

                          <Tooltip title={CATEGORY_INFO[scope].tooltip} placement="right" arrow>
                            <Box>
                              <HelpOutlineIcon
                                sx={{ fontSize: 16, color: '#8c8c8c', cursor: 'pointer' }}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText primary="НЕ устанавливайте флажок «Только на чтение»" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Если у вас нет доступа к управлению API-ключами, запросите у владельца магазина новый API-ключ по указанным правилам" />
                </ListItem>
              </List>
            </Box>
            <Stack direction="row" flexBasis="100%" justifyContent="space-between">
              <Box sx={{ display: 'flex' }}>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!watch('apiKey')?.trim() && !moduleTypes.length}
                  isLoading={isSubmitting}
                >
                  Сохранить
                </Button>
                <Button sx={{ marginLeft: '12px' }} onClick={handleClose} variant="secondary">
                  Отменить
                </Button>
              </Box>
              {/* <InstructionButton embedLink="#" /> */}
            </Stack>
          </form>
        </Box>
      </Box>
    </>
  );
};

interface ChangeTokenModalProps {
  open: boolean;
  mode: 'add' | 'edit';
  onClose: () => void;
  onSubmitEnd?: () => void;
  listText?: string;
  listHidden?: boolean;
  refreshOptions?: RefreshOptions;
}
